import { useReplaceLocation } from '../contact'
import { type AddHookResponse, mutationAdd } from '../hooks'
import { useLocationCache } from '../location'
import { useTantalimMutation } from '../mutation'
import { type OnLocationMutate } from './types'
import {
  type LatLng,
  Logger,
  type ExtendedLocationData,
  type LocationId,
  toResponseError,
  type LocationDispositionSuccess,
  type LocationDispositionInput as LocDispInput,
} from '@terros/common'

const logger = new Logger('useAddLocationDisposition')
logger.level = 'verbose'

type LocationDispositionInput = LocDispInput & { locationId: LocationId; deviceLocation: LatLng }

export function useAddLocationDisposition(
  onMutate: OnLocationMutate,
  confirm: (error: string, message: string | undefined) => Promise<void>
): AddHookResponse<LocationDispositionInput, ExtendedLocationData> {
  const replaceLocation = useReplaceLocation()
  const { replaceItem } = useLocationCache()

  const mutation = useTantalimMutation<LocationDispositionInput, LocationDispositionSuccess>({
    url: '/location/disposition',
  })

  async function add(input: LocationDispositionInput): Promise<ExtendedLocationData> {
    try {
      const { location: newLocation } = await mutation.mutateAsync({ version: 3, ...input })
      logger.verbose('saved location', newLocation)

      replaceLocation({
        locationId: input.locationId,
        newLocation,
      })

      replaceItem(input.locationId, newLocation)
      await onMutate(input.locationId, newLocation.locationId)
      return newLocation
    } catch (error) {
      logger.error('error adding location', error)
      const responseError = toResponseError(error)
      if (responseError.error === 'Duplicate') {
        await confirm(
          'Duplicate Account',
          'Someone else has already created an account at this location. Are you sure you want to create another?'
        )
        return add({ ...input, bypassDuplicateCheck: true })
      }
      throw toResponseError(error)
    }
  }

  return mutationAdd(mutation, add)
}
